import { areObjectIdsEqual, isObjectIdString } from 'helpers/idComparison';
import type { DocumentationModuleLoadPlan } from './types';

/**
 * Decides how an opening module instance treats the store, given the project
 * loaded so far and whether no other instance is open (module re-entered).
 */
export function resolveDocumentationModuleLoad({
  projectId,
  loadedProjectId,
  isFirstOpenInstance,
}: {
  projectId: string | undefined;
  loadedProjectId: string | undefined;
  isFirstOpenInstance: boolean;
}): DocumentationModuleLoadPlan {
  const isSameProject = areObjectIdsEqual(loadedProjectId, projectId);
  const hasOtherProjectData = isObjectIdString(loadedProjectId) && !isSameProject;

  if (!isObjectIdString(projectId)) {
    return { resetStore: hasOtherProjectData, loadProject: false };
  }
  if (hasOtherProjectData) {
    return { resetStore: true, loadProject: true };
  }
  // Same (or no) project loaded: load when nothing is loaded yet or when the
  // module is entered again; nested instances reuse the loaded data.
  return {
    resetStore: false,
    loadProject: !isSameProject || isFirstOpenInstance,
  };
}

/**
 * Captures the current project data generation; the returned check tells
 * whether the project changed since (the async result must be dropped).
 */
export function createProjectChangeCheck(
  get: () => { projectDataGeneration: number }
): () => boolean {
  const generation = get().projectDataGeneration;
  return () => get().projectDataGeneration !== generation;
}
